import classNames from 'classnames'
import React, { FC, useCallback, useEffect, useState } from 'react'

import { Notification, NotificationData } from './notification.js'

export interface NotificationContainerProps {
    notifications: NotificationData[]
    onClose: (id: string) => void
    /** Translated accessible name of each notification's close button. */
    closeLabel: string
}

interface AutoDismissNotificationProps {
    notification: NotificationData
    closeLabel: string
    triggerExit: () => void
}

const AUTO_DISMISS_DELAY = 2000
const EXIT_ANIMATION_DURATION = 250

const AutoDismissNotification: React.FC<AutoDismissNotificationProps> = ({
    notification,
    closeLabel,
    triggerExit,
}) => {
    useEffect(() => {
        const timer = setTimeout(triggerExit, AUTO_DISMISS_DELAY)
        return () => clearTimeout(timer)
    }, [notification.id, triggerExit])

    return (
        <Notification
            {...notification}
            onClose={triggerExit}
            closeLabel={closeLabel}
            duration={AUTO_DISMISS_DELAY}
        />
    )
}

export const NotificationContainer: FC<NotificationContainerProps> = ({
    notifications,
    onClose,
    closeLabel,
}) => {
    const [exiting, setExiting] = useState<Record<string, boolean>>({})

    const startExit = useCallback(
        (id: string) => {
            setExiting((prev) => ({ ...prev, [id]: true }))
            setTimeout(() => onClose(id), EXIT_ANIMATION_DURATION)
        },
        [onClose],
    )
    const makeExitHandler = useCallback((id: string) => () => startExit(id), [startExit])

    return (
        <div
            className="notification__container"
            style={
                {
                    '--notification-exit-duration': `${EXIT_ANIMATION_DURATION}ms`,
                } as React.CSSProperties
            }
        >
            {notifications.map((notification: NotificationData) => (
                <div
                    key={notification.id}
                    className={classNames(
                        'notification__item',
                        exiting[notification.id] && 'notification__item--exiting',
                    )}
                >
                    <AutoDismissNotification
                        notification={notification}
                        closeLabel={closeLabel}
                        triggerExit={makeExitHandler(notification.id)}
                    />
                </div>
            ))}
        </div>
    )
}
